import React, { useCallback, useContext, useState } from 'react';
import { Alert, Button, Modal, Select, Spin } from 'antd';

import AmazonWebServicesContext from '@totem/components/administration/amazonWebServices/AmazonWebServicesContext';
import { SalesOrderLine } from '@totem/components/salesOrders/list/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { debounce } from '@totem/utilities/debounce';
import {
  AWS_INSTANCES_ENDPOINT,
  SALES_ORDERS_ENDPOINT,
} from '@totem/utilities/endpoints';

const DEBOUNCE_TIME = 500;

const describeLine = (line: SalesOrderLine) =>
  `${line.nsSalesOrder?.tranId ?? ''}:${line.nsSalesOrderLine?.line ?? ''} — ${(
    line.services ?? []
  )
    .map((svc) => svc.name)
    .join(', ')}`;

const AssignSalesOrderModal = () => {
  const { getApiToken } = useContext(UserProfileContext);
  const { selectedInstance, activeAction, onActionClose, onRefresh } =
    useContext(AmazonWebServicesContext);
  const [searching, setSearching] = useState<boolean>(false);
  const [options, setOptions] = useState<SalesOrderLine[]>([]);
  const [selectedLine, setSelectedLine] = useState<SalesOrderLine | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const runSearch = useCallback(
    (term: string) => {
      if (!term) {
        setOptions([]);
        return;
      }

      setSearching(true);

      fetch(SALES_ORDERS_ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getApiToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paging: { page: 1, pageSize: 20 },
          sort: { field: 'nsSalesOrder.tranId', direction: 1 },
          filters: [
            { field: 'status', values: ['Active'] },
            {
              field: 'nsSalesOrder.tranId',
              operator: 'cscontains',
              values: [term],
            },
          ],
        }),
      })
        .then((res) => res.json())
        .then((result: { lines: SalesOrderLine[] }) =>
          setOptions(result.lines ?? []),
        )
        .finally(() => setSearching(false));
    },
    [getApiToken],
  );

  const handleSearch = useCallback(debounce(runSearch, DEBOUNCE_TIME), [
    runSearch,
  ]);

  if (activeAction !== 'salesOrder' || !selectedInstance) {
    return null;
  }

  const handleSelect = (value: string) => {
    setSelectedLine(options.find((option) => option.id === value) ?? null);
  };

  const handleClose = () => {
    setOptions([]);

    setSelectedLine(null);

    onActionClose();
  };

  const handleSave = () => {
    if (!selectedLine) {
      return;
    }

    setSaving(true);

    const services =
      selectedLine.services.length > 0
        ? selectedLine.services
        : [{ id: '', name: 'General' }];

    Promise.all(
      services.map((service) =>
        fetch(
          `${AWS_INSTANCES_ENDPOINT}/${selectedInstance.item.instance.instanceId}/salesOrders`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${getApiToken()}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              salesOrderId: selectedLine.id,
              salesOrderNumber: selectedLine.nsSalesOrder?.tranId,
              lineNumber: selectedLine.nsSalesOrderLine?.line,
              service: service.name,
            }),
          },
        ),
      ),
    ).then(() => {
      setSaving(false);

      handleClose();

      onRefresh();
    });
  };

  return (
    <Modal
      open
      title={<ModalTitle>Assign Sales Order</ModalTitle>}
      onCancel={handleClose}
      footer={
        <ModalFooter>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!selectedLine}
          >
            Assign
          </Button>
        </ModalFooter>
      }
    >
      <p>
        Instance: <strong>{selectedInstance.name}</strong>
      </p>
      <Select
        showSearch
        style={{ width: '100%' }}
        placeholder="Search Active sales orders by order number..."
        filterOption={false}
        notFoundContent={searching ? <Spin size="small" /> : 'No results'}
        onSearch={handleSearch}
        onChange={handleSelect}
        options={options.map((line) => ({
          value: line.id,
          label: describeLine(line),
        }))}
      />
      {selectedLine && (
        <Alert
          style={{ marginTop: 12 }}
          type="info"
          showIcon
          message={`Will assign ${describeLine(selectedLine)}`}
        />
      )}
    </Modal>
  );
};

export default AssignSalesOrderModal;
